// Check if 'single-picker' custom element is not already defined
if (!customElements.get('single-picker')) {
  class SinglePicker extends HTMLElement {
    constructor() {
      super();
      this.initializeElements();
      this.setInitialPickedVariant();
      this.addEventListeners();
    }

    // Initialize and cache relevant DOM elements
    initializeElements() {
      this.items = this.querySelectorAll('.single-picker__item');
      this.labels = this.querySelectorAll('.single-picker__label');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.currentVariantIds = document.querySelectorAll('.product-variant-id');
      this.infos = document.querySelectorAll('.product-variant-infos');
    }

    // Add click event listeners to picker items
    addEventListeners() {
      this.items.forEach((item, index) => {
        item.addEventListener('click', () => this.handleItemClick(item, index));

        // role="radio" needs Enter/Space wired up by hand, plus arrow keys to
        // move through the group the way a native radio group does.
        item.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.handleItemClick(item, index);
            return;
          }

          const forward = event.key === 'ArrowRight' || event.key === 'ArrowDown';
          const back = event.key === 'ArrowLeft' || event.key === 'ArrowUp';
          if (!forward && !back) return;

          event.preventDefault();
          const count = this.items.length;
          const next = forward ? (index + 1) % count : (index - 1 + count) % count;
          this.items[next].focus();
          this.handleItemClick(this.items[next], next);
        });
      });
    }

    // Handle item click: update UI and set the current variant
    handleItemClick(item, index) {
      this.clearCheckedAttributes();
      this.setCheckedAttributes(item, index);
      this.setInfos(index);
      this.updateCurrentVariantIds(item.dataset.id);
      this.updateURL(item.dataset.url, item.dataset.id);
      this.updateMedia(item.dataset.media);
      this.renderProductInfo(item.dataset.id);
    }

    // Clear "data-checked" attributes from items and labels
    clearCheckedAttributes() {
      this.items.forEach(item => {
        item.removeAttribute('data-checked');
        item.setAttribute('aria-checked', 'false');
      });
      this.labels.forEach(label => label?.removeAttribute('data-checked'));
    }

    // Set "data-checked" attributes for the clicked item and its label
    setCheckedAttributes(item, index) {
      item.setAttribute('data-checked', '');
      item.setAttribute('aria-checked', 'true');
      this.labels[index]?.setAttribute('data-checked', '');
    }

    // Set active infos

    setInfos(index) {
      if (this.infos.length > 0) {
        this.infos.forEach(item => item.setAttribute('data-active', 'false'));
        this.infos[index].setAttribute('data-active', 'true');
      }
    }

    // Update the value of all current variant ID elements
    updateCurrentVariantIds(variantId) {
      this.currentVariantIds.forEach(id => {
        id.value = variantId;
      });
    }

    // Update the browser URL to reflect the selected variant
    updateURL(url, variantId) {
      window.history.replaceState({}, '', `${url}?variant=${variantId}`);
    }

    // Set the initially picked variant based on "data-checked" attribute
    setInitialPickedVariant() {
      const urlParams = new URLSearchParams(window.location.search);
      const variantId = urlParams.get('variant');

      if (variantId) {
        const matchedItem = Array.from(this.items).find(
          item => item.dataset.id === variantId
        );

        if (matchedItem) {
          this.items.forEach(item => {
            item.removeAttribute('data-checked');
            item.setAttribute('aria-checked', 'false');
          });
          matchedItem.setAttribute('data-checked', '');
          matchedItem.setAttribute('aria-checked', 'true');
          this.setInfos(Array.from(this.items).indexOf(matchedItem));
          return;
        }
      }

      const defaultItem = this.items[this.dataset.index - 1];

      if (defaultItem) {
        defaultItem.setAttribute('data-checked', '');
        defaultItem.setAttribute('aria-checked', 'true');
        this.setInfos(this.dataset.index - 1);
        setTimeout(() => {
          this.updateMedia(defaultItem.dataset.media);
        }, 0);
        this.renderProductInfo(defaultItem.dataset.id);
        this.updateCurrentVariantIds(defaultItem.dataset.id);
      }
    }

    //  render product info block
    renderProductInfo(variantId) {
      const sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section;
      fetch(
        `${this.dataset.url}?variant=${variantId}&section_id=${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section
        }`
      )
        .then((response) => response.text())
        .then((responseText) => {

          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const destination = document.getElementById(`price-${this.dataset.section}`);
          const source = html.getElementById(
            `price-${this.dataset.section}`
          );
          const skuSource = html.getElementById(
            `Sku-${this.dataset.section}`
          );
          const skuDestination = document.getElementById(`Sku-${this.dataset.section}`);
          const inventorySource = html.getElementById(
            `Inventory-${this.dataset.section}`
          );
          const inventoryDestination = document.getElementById(`Inventory-${this.dataset.section}`);

          const volumePricingSource = html.getElementById(
            `Volume-${this.dataset.section}`
          );

          const pricePerItemDestination = document.getElementById(`Price-Per-Item-${this.dataset.section}`);
          const pricePerItemSource = html.getElementById(`Price-Per-Item-${this.dataset.section}`);

          const volumePricingDestination = document.getElementById(`Volume-${this.dataset.section}`);

          if (source && destination) destination.innerHTML = source.innerHTML;
          if (inventorySource && inventoryDestination) inventoryDestination.innerHTML = inventorySource.innerHTML;

          // Update the add-to-cart button (text + disabled/sold-out state)
          const buttonSource = html.getElementById(`ProductSubmitButton-${this.dataset.section}`);
          const buttonDestination = document.getElementById(`ProductSubmitButton-${this.dataset.section}`);
          if (buttonSource && buttonDestination) {
            buttonDestination.innerHTML = buttonSource.innerHTML;
            if (buttonSource.hasAttribute('disabled')) {
              buttonDestination.setAttribute('disabled', '');
            } else {
              buttonDestination.removeAttribute('disabled');
            }
          }

          // Update the in-stock / sold-out label
          const inStockSource = html.getElementById(`InStock-${this.dataset.section}`);
          const inStockDestination = document.getElementById(`InStock-${this.dataset.section}`);
          if (inStockSource && inStockDestination) {
            inStockDestination.innerHTML = inStockSource.innerHTML;
          }

          if (skuSource && skuDestination) {
            skuDestination.innerHTML = skuSource.innerHTML;
            skuDestination.classList.toggle('visibility-hidden', skuSource.classList.contains('visibility-hidden'));
          }

          if (volumePricingSource && volumePricingDestination) {
            volumePricingDestination.innerHTML = volumePricingSource.innerHTML;
          }

          if (pricePerItemSource && pricePerItemDestination) {
            pricePerItemDestination.innerHTML = pricePerItemSource.innerHTML;
            pricePerItemDestination.classList.toggle('visibility-hidden', pricePerItemSource.classList.contains('visibility-hidden'));
          }

          const price = document.getElementById(`price-${this.dataset.section}`);

          if (price) price.classList.remove('visibility-hidden');

          if (inventoryDestination)
            inventoryDestination.classList.toggle('visibility-hidden', inventorySource.innerText === '');

          if (typeof window.applyPdpSitecodePricing === 'function') {
            setTimeout(() => window.applyPdpSitecodePricing(), 0);
          }

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId,
              html,
              variant: variantId,
            },
          });
        });
    }

    //   update image in slider
    updateMedia(mediaId) {
      if (!mediaId) return;

      const mediaGalleries = document.querySelectorAll(`[id^="MediaGallery-${this.dataset.section}"]`);
      mediaGalleries.forEach((mediaGallery) => {
        mediaGallery.setActiveMedia(`${this.dataset.section}-${mediaId}`, true)
      });


      const modalContent = document.querySelector(`#ProductModal-${this.dataset.section} .product-media-modal__content`);
      if (!modalContent) return;
      const newMediaModal = modalContent.querySelector(`[data-media-id="${mediaId}"]`);
      modalContent.prepend(newMediaModal);
    }
  }

  // Define the 'single-picker' custom element
  customElements.define('single-picker', SinglePicker);
}
